// eslint-disable-next-line boundaries/element-types
import { mapConversationsAgentToAgentId } from "@mirage/service-conversation/types";
import { tagged } from "@mirage/service-logging";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import type { Observable } from "rxjs";
import { of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { v5 as uuidv5 } from "uuid";
import { createQuerySuggestionsObservable } from "./context_observables";
import type { QuerySuggestionsResponse } from "./gen/context_engine_conversations_service_pb";
import type { SuggestedQuery } from "./gen/conversations_pb";
import type { ClientContext } from "./gen/models_pb";
import type { QuerySuggestionResult, QuerySuggestionsResult } from "./types";

const logger = tagged("conversations_grpc");

const QUERY_SUGGESTION_NAMESPACE = "6d70c04e-6bc8-43d1-b936-e940c80ef465";

export function toQuerySuggestionResult(requestId: string) {
  return function (
    querySuggestion: SuggestedQuery,
  ): QuerySuggestionResult | undefined {
    if (!querySuggestion.text) {
      logger.warn("querySuggestion.text is undefined");
      return;
    }

    const { agent } = querySuggestion;
    const agentIds =
      agent && mapConversationsAgentToAgentId[agent]
        ? mapConversationsAgentToAgentId[agent]
        : undefined;

    return {
      ...querySuggestion,
      uuid: uuidv5(querySuggestion.text!, QUERY_SUGGESTION_NAMESPACE),
      assistRequestId: requestId,
      agentIds,
    };
  };
}

export function genQuerySuggestions(
  userQuery?: string,
  documentIds?: string[],
  experimentSetting?: string,
  source?: string,
  clientContext?: ClientContext,
  debug?: boolean,
  stackId?: string,
  maxSuggestions?: number,
): Observable<QuerySuggestionsResult> {
  const querySuggestions$ = createQuerySuggestionsObservable(
    userQuery,
    documentIds,
    experimentSetting,
    source,
    clientContext,
    debug,
    stackId,
    maxSuggestions,
  );

  return querySuggestions$.pipe(
    map((response: QuerySuggestionsResponse): QuerySuggestionsResult => {
      return {
        ...response,
        querySuggestions: response.querySuggestions
          .map(toQuerySuggestionResult(response.requestId))
          .filter(isDefined),
        timestamp: new Date().toISOString(),
      };
    }),
    catchError((error) => {
      logger.error("genQuerySuggestions within observable error", error);
      const errorSummary: QuerySuggestionsResult = {
        error: "Failed to fetch query suggestions. " + error,
        timestamp: new Date().toISOString(),
        querySuggestions: [],
      };
      return of(errorSummary);
    }),
  );
}
